
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWedding } from '../context/WeddingContext';
import { supabase, getAuthenticatedSupabase } from '../supabaseClient';
import { getTranslation } from '../i18n';
import { Modal } from '../components/Modal';
import { SearchAndFilter } from '../components/SearchAndFilter';
import { WeddingsSections } from '../components/weddings/WeddingsSections';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Check } from 'lucide-react';
import { groupWeddings } from '../lib/weddings/categorize';
import { Wedding } from '../types';
import apiClient from '../lib/api/client';
import { ALL_USERS_PRO_PROMO } from '../constants';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';

export default function WeddingsPage() {
  const { user, profile, accessToken, isSuperAdmin } = useAuth();
  const { weddings, refreshWeddings } = useWedding();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberRoles, setMemberRoles] = useState<Record<string, string>>({});
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [deleteWeddingId, setDeleteWeddingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editWedding, setEditWedding] = useState<Wedding | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const navigate = useNavigate();

  const locale = profile?.language || 'cs';
  const t = (key: string) => getTranslation(locale, key);

  // Fetch wedding members to determine user's role for each wedding
  useEffect(() => {
    const fetchMemberRoles = async () => {
      if (!user || !user.id) {
        setIsLoadingMembers(false);
        return;
      }

      try {
        const supabaseAuth = getAuthenticatedSupabase();
        const { data, error } = await supabaseAuth
          .from('wedding_members')
          .select('wedding_id, role')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching wedding members:', error);
          setIsLoadingMembers(false);
          return;
        }

        // Create a map: wedding_id -> role
        const rolesMap: Record<string, string> = {};
        if (data) {
          for (const member of data) {
            rolesMap[member.wedding_id] = member.role;
          }
        }
        setMemberRoles(rolesMap);
      } catch (err) {
        console.error('Error fetching wedding members:', err);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    fetchMemberRoles();
  }, [user, weddings]);

  // Filter weddings by search query (title + location)
  const filteredWeddings = useMemo(() => {
    if (!searchQuery.trim()) {
      return weddings;
    }

    const query = searchQuery.toLowerCase().trim();
    return weddings.filter((wedding) => {
      const titleMatch = wedding.title.toLowerCase().includes(query);
      const locationMatch = (wedding.location || '').toLowerCase().includes(query);
      return titleMatch || locationMatch;
    });
  }, [weddings, searchQuery]);

  // Group filtered weddings into categories
  const groupedWeddings = useMemo(() => {
    return groupWeddings(filteredWeddings);
  }, [filteredWeddings]);

  const createWedding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || isSubmitting) return;
    
    if (!title.trim()) {
      alert('Zadejte prosím název svatby.');
      return;
    }

    setIsSubmitting(true);

    try {
      const slug = title.toLowerCase().trim().replace(/\s+/g, '-') + '-' + Math.random().toString(36).substr(2, 4);
      const isDemoUser = user.id === 'debug-user-id';
      const ownerId = isDemoUser ? null : user.id;

      // Use fresh client with current token so RLS sees auth.uid()
      const supabaseAuth = getAuthenticatedSupabase();

      const { data: weddingData, error: weddingError } = await supabaseAuth.from('weddings').insert({
        title: title.trim(),
        date: date || null,
        location: (location && location.trim()) || 'Neurčeno',
        owner_user_id: ownerId,
        public_slug: slug,
        default_language: locale,
        ...(ALL_USERS_PRO_PROMO ? { plan: 'PRO', is_free_tier: false } : {}),
      }).select();

      if (weddingError) throw weddingError;
      const wedding = weddingData?.[0];

      if (!wedding) return;

      if (!isDemoUser) {
        await supabaseAuth.from('wedding_members').insert({
          wedding_id: wedding.id,
          user_id: user.id,
          role: 'admin'
        });
      }

      setIsModalOpen(false);
      setTitle('');
      setDate('');
      setLocation('');
      await refreshWeddings();
      navigate(`/app/w/${wedding.id}/dashboard`);
    } catch (err: any) {
      console.error('Error creating wedding:', err);
      alert(`Chyba: ${err.message || 'Nepodařilo se vytvořit svatbu.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWeddingClick = (weddingId: string) => {
    navigate(`/app/w/${weddingId}/dashboard`);
  };

  const handleDeleteClick = (weddingId: string) => {
    setDeleteWeddingId(weddingId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteWeddingId || !user) return;

    setIsDeleting(true);
    try {
      // Use auth token from AuthContext
      if (accessToken) {
        apiClient.setAuthToken(accessToken);
      }

      const { error } = await apiClient.deleteWedding(deleteWeddingId);

      if (error) {
        throw new Error(error);
      }

      // Optimistically remove from local state
      await refreshWeddings();
      setDeleteWeddingId(null);
      setToast({
        id: Date.now().toString(),
        type: 'success',
        text: 'Svatba byla úspěšně smazána',
      });
    } catch (err: any) {
      console.error('Error deleting wedding:', err);
      setToast({
        id: Date.now().toString(),
        type: 'error',
        text: err.message || 'Nepodařilo se smazat svatbu',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteWeddingId(null);
  };

  const handleEditClick = (wedding: Wedding) => {
    setEditWedding(wedding);
    setEditTitle(wedding.title || '');
    setEditDate(wedding.date ? wedding.date.slice(0, 10) : '');
    setEditLocation(wedding.location || '');
  };

  const handleEditClose = () => {
    setEditWedding(null);
    setEditTitle('');
    setEditDate('');
    setEditLocation('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editWedding || !user || isSavingEdit) return;
    if (!editTitle.trim()) {
      setToast({ id: Date.now().toString(), type: 'error', text: 'Zadejte název svatby.' });
      return;
    }

    setIsSavingEdit(true);
    try {
      if (accessToken) apiClient.setAuthToken(accessToken);
      const { error } = await apiClient.updateWedding(editWedding.id, {
        title: editTitle.trim(),
        date: editDate || null,
        location: (editLocation && editLocation.trim()) || null,
      });
      if (error) throw new Error(error);
      await refreshWeddings();
      handleEditClose();
      setToast({ id: Date.now().toString(), type: 'success', text: 'Svatba byla upravena.' });
    } catch (err: any) {
      console.error('Error updating wedding:', err);
      setToast({ id: Date.now().toString(), type: 'error', text: err.message || 'Nepodařilo se uložit změny.' });
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in relative z-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">Moje svatby</h1>
          <p className="text-ds-body text-slate-400 dark:text-slate-500 mt-2">Přehled akcí, kde máte roli</p>
        </div>
        <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
          Nová svatba
        </Button>
      </div>

      {/* Search */}
      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Vyhledat svatbu…"
      />

      {/* Weddings Sections */}
      {!isLoadingMembers && (
        <>
          {filteredWeddings.length === 0 ? (
            <Card className="py-12 text-center">
              <p className="text-ds-body text-slate-400 dark:text-slate-500">
                {searchQuery ? 'Žádné svatby neodpovídají vašemu vyhledávání.' : 'Zatím jste nevytvořili žádnou svatbu.'}
              </p>
            </Card>
          ) : (
            <WeddingsSections
              grouped={groupedWeddings}
              userId={user?.id || ''}
              memberRoles={memberRoles}
              isSuperAdmin={isSuperAdmin}
              onWeddingClick={handleWeddingClick}
              onWeddingEdit={handleEditClick}
              onWeddingDelete={handleDeleteClick}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteWeddingId !== null}
        title="Smazat svatbu?"
        description="Tuto akci nelze vrátit zpět."
        confirmLabel={isDeleting ? 'Mazání...' : 'Smazat'}
        cancelLabel="Zrušit"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Toast */}
      {toast && (
        <Toast
          toast={toast}
          onClose={() => setToast(null)}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nová svatba"
        maxWidth="max-w-lg"
      >
        <div className="text-center mb-8">
            <p className="text-ds-body text-slate-400 dark:text-slate-500">Vytvořte si nový pracovní prostor pro svou svatbu.</p>
        </div>

        <form onSubmit={createWedding} className="space-y-5">
            <Input 
              label="Název svatby"
              type="text" 
              required 
              autoFocus
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              placeholder="Např. Svatba Anny a Petra" 
            />
            <Input 
              label="Předpokládané datum"
              type="date" 
              required 
              value={date} 
              onChange={e => setDate(e.target.value)} 
            />
            <Input 
              label="Místo konání"
              type="text" 
              value={location} 
              onChange={e => setLocation(e.target.value)} 
              placeholder="Např. Zámek Hluboká, restaurace..." 
            />
            <div className="pt-6">
              <Button 
                type="submit" 
                disabled={isSubmitting || !user}
                isLoading={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Ukládání...' : 'Začít plánovat'}
              </Button>
            </div>
        </form>
      </Modal>

      {/* Edit wedding modal */}
      <Modal
        isOpen={editWedding !== null}
        onClose={handleEditClose}
        title="Upravit svatbu"
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleEditSubmit} className="space-y-5">
          <Input
            label="Název svatby"
            type="text"
            required
            autoFocus
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Např. Svatba Anny a Petra"
          />
          <Input
            label="Předpokládané datum"
            type="date"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
          />
          <Input
            label="Místo konání"
            type="text"
            value={editLocation}
            onChange={(e) => setEditLocation(e.target.value)}
            placeholder="Např. Zámek Hluboká, restaurace..."
          />
          <div className="pt-6 flex gap-3">
            <Button type="button" variant="secondary" onClick={handleEditClose} className="flex-1">
              Zrušit
            </Button>
            <Button type="submit" disabled={isSavingEdit} isLoading={isSavingEdit} className="flex-1">
              {isSavingEdit ? 'Ukládání...' : 'Uložit'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
